import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall } from '../../config/api';
import moment from 'moment';
import { FaSave, FaTimes, FaPlus, FaTrash } from 'react-icons/fa';

interface Patient {
  id: number;
  first_name: string;
  last_name: string;
  patient_id: string;
  date_of_birth: string;
  insurance_type?: string;
}

interface Provider {
  id: number;
  full_name: string;
  provider_number: string;
}

interface BillingTemplate {
  id: number;
  name: string;
  items: MBSItem[];
}

interface MBSItem {
  id: number;
  item_number: string;
  description: string;
  fee: number;
  category: string;
}

interface InvoiceItem {
  id?: number;
  mbs_item_number: string;
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
}

interface RecentActivity {
  id: number;
  type: 'appointment' | 'procedure';
  date: string;
  description: string;
  provider: string;
}

const InvoiceCreate: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();

  // Invoice Details
  const [patientId, setPatientId] = useState<number | null>(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [patients, setPatients] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [showPatientSearch, setShowPatientSearch] = useState(false);

  const [providerId, setProviderId] = useState<number | null>(null);
  const [providers, setProviders] = useState<Provider[]>([]);
  
  const [billTo, setBillTo] = useState<'patient' | 'insurer'>('patient');
  const [insurerName, setInsurerName] = useState('');
  
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [templates, setTemplates] = useState<BillingTemplate[]>([]);
  
  const [invoiceDate, setInvoiceDate] = useState(moment().format('YYYY-MM-DD'));
  const [paymentTerms, setPaymentTerms] = useState('14'); // days
  const [dueDate, setDueDate] = useState(moment().add(14, 'days').format('YYYY-MM-DD'));
  
  const [accountType, setAccountType] = useState('');
  const [reference, setReference] = useState('');

  // Invoice Items
  const [items, setItems] = useState<InvoiceItem[]>([
    {
      mbs_item_number: '',
      description: '',
      quantity: 1,
      unit_price: 0,
      total: 0
    }
  ]);

  // MBS Item Search
  const [mbsSearch, setMbsSearch] = useState('');
  const [mbsItems, setMbsItems] = useState<MBSItem[]>([]);
  const [showMbsSearch, setShowMbsSearch] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState<number | null>(null);

  // Notes
  const [notes, setNotes] = useState('');

  // Recent patient activity
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);

  const [saving, setSaving] = useState(false);

  // Search patients
  useEffect(() => {
    if (patientSearch.length >= 2) {
      searchPatients();
    }
  }, [patientSearch]);

  const searchPatients = async () => {
    if (!token) return;
    
    try {
      const response = await apiCall(
        `/patients/?search=${patientSearch}`,
        { method: 'GET' },
        token || undefined
      );
      const data = await response.json();
      setPatients(data.results || data || []);
    } catch (error) {
      console.error('Error searching patients:', error);
    }
  };

  // Search MBS items
  useEffect(() => {
    if (mbsSearch.length >= 2) {
      searchMbsItems();
    }
  }, [mbsSearch]);

  const searchMbsItems = async () => {
    if (!token) return;
    
    try {
      const response = await apiCall(
        `/billing/mbs-items/?search=${mbsSearch}`,
        { method: 'GET' },
        token || undefined
      );
      const data = await response.json();
      setMbsItems(data.results || data || []);
    } catch (error) {
      console.error('Error searching MBS items:', error);
      // Mock data for demo
      setMbsItems([
        { id: 1, item_number: '104', description: 'Professional attendance - Initial', fee: 85.00, category: 'Consultation' },
        { id: 2, item_number: '105', description: 'Professional attendance - Standard', fee: 38.75, category: 'Consultation' },
        { id: 3, item_number: '110', description: 'Professional attendance - Long', fee: 145.00, category: 'Consultation' },
      ]);
    }
  };

  const handlePatientSelect = (patient: Patient) => {
    setSelectedPatient(patient);
    setPatientId(patient.id);
    setPatientSearch(`${patient.first_name} ${patient.last_name} (${patient.patient_id})`);
    setShowPatientSearch(false);
    loadRecentActivity(patient.id);
  };

  const loadRecentActivity = async (patientId: number) => {
    if (!token) return;
    
    try {
      // Load recent appointments for this patient
      const response = await apiCall(
        `/scheduling/appointments/?patient_id=${patientId}&limit=10`,
        { method: 'GET' },
        token || undefined
      );
      const data = await response.json();
      
      // Transform appointments into recent activity
      const activities: RecentActivity[] = (data.results || data || []).map((apt: any) => ({
        id: apt.id,
        type: apt.procedure_description ? 'procedure' : 'appointment',
        date: apt.start_time,
        description: apt.title || apt.procedure_description || 'Consultation',
        provider: apt.provider_name || 'Unknown Provider'
      }));
      
      setRecentActivity(activities);
    } catch (error) {
      console.error('Error loading recent activity:', error);
      setRecentActivity([]);
    }
  };

  const handleMbsItemSelect = (mbsItem: MBSItem, index: number) => {
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      mbs_item_number: mbsItem.item_number,
      description: mbsItem.description,
      unit_price: mbsItem.fee,
      total: mbsItem.fee * newItems[index].quantity
    };
    setItems(newItems);
    setShowMbsSearch(false);
    setCurrentItemIndex(null);
    setMbsSearch('');
  };

  const handleItemChange = (index: number, field: keyof InvoiceItem, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Recalculate total
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
    }
    
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, {
      mbs_item_number: '',
      description: '',
      quantity: 1,
      unit_price: 0,
      total: 0
    }]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + item.total, 0);
  };

  const calculateGST = () => {
    return calculateSubtotal() * 0.1; // 10% GST
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateGST();
  };

  const handleSave = async (saveStatus: 'draft' | 'sent') => {
    if (!patientId) {
      alert('Please select a patient');
      return;
    }

    if (items.length === 0 || items.every(item => !item.mbs_item_number)) {
      alert('Please add at least one invoice item');
      return;
    }

    setSaving(true);
    try {
      const invoiceData = {
        patient: patientId,
        invoice_date: invoiceDate,
        due_date: dueDate,
        status: saveStatus,
        notes,
        items: items.filter(item => item.mbs_item_number), // Only include items with MBS numbers
        subtotal: calculateSubtotal(),
        gst: calculateGST(),
        total: calculateTotal()
      };

      const response = await apiCall(
        '/billing/invoices/',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(invoiceData)
        },
        token || undefined
      );

      const data = await response.json();
      navigate(`/billing/invoices/${data.id}`);
    } catch (error) {
      console.error('Error creating invoice:', error);
      alert('Failed to create invoice. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  return (
    <div className="flex h-screen bg-base-200">
      {/* Main Content - Full Width */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-base-content">Create Invoice</h1>
            <div className="flex gap-2">
              <button onClick={() => navigate('/billing/invoices')} className="btn btn-sm btn-ghost" disabled={saving}>
                <FaTimes className="mr-1" /> Cancel
              </button>
              <button onClick={() => handleSave('draft')} className="btn btn-sm btn-outline" disabled={saving}>
                <FaSave className="mr-1" /> Draft
              </button>
              <button onClick={() => handleSave('sent')} className="btn btn-sm btn-primary" disabled={saving}>
                <FaSave className="mr-1" /> {saving ? 'Saving...' : 'Save & Send'}
              </button>
            </div>
          </div>

          {/* Main Invoice Form */}
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body p-4">
              {/* Top Row - Patient and Dates */}
              <div className="grid grid-cols-6 gap-3 mb-3">
                <div className="col-span-3 form-control">
                  <label className="label py-1"><span className="label-text text-sm font-semibold">Patient *</span></label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search patient..."
                      className="input input-sm input-bordered w-full"
                      value={patientSearch}
                      onChange={(e) => setPatientSearch(e.target.value)}
                      onFocus={() => setShowPatientSearch(true)}
                    />
                    {showPatientSearch && patients.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {patients.map(patient => (
                          <div key={patient.id} className="p-2 hover:bg-base-200 cursor-pointer" onClick={() => handlePatientSelect(patient)}>
                            <div className="font-semibold text-sm">{patient.first_name} {patient.last_name}</div>
                            <div className="text-xs opacity-60">ID: {patient.patient_id} | DOB: {moment(patient.date_of_birth).format('DD/MM/YYYY')}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="form-control">
                  <label className="label py-1"><span className="label-text text-sm font-semibold">Invoice Date</span></label>
                  <input type="date" className="input input-sm input-bordered" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
                </div>
                
                <div className="form-control">
                  <label className="label py-1"><span className="label-text text-sm font-semibold">Due Date</span></label>
                  <input type="date" className="input input-sm input-bordered" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                </div>
                
                <div className="form-control">
                  <label className="label py-1"><span className="label-text text-sm font-semibold">Total</span></label>
                  <div className="input input-sm input-bordered bg-base-200 font-bold text-primary flex items-center">
                    {formatCurrency(calculateTotal())}
                  </div>
                </div>
              </div>

              <div className="divider my-2">Invoice Items</div>

              {/* Invoice Items - Compact Table */}
              <div className="overflow-x-auto">
                <table className="table table-xs">
                  <thead>
                    <tr>
                      <th className="w-20">MBS Item</th>
                      <th>Description</th>
                      <th className="w-16">Qty</th>
                      <th className="w-24">Price</th>
                      <th className="w-24">Total</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <div className="relative">
                            <input
                              type="text"
                              className="input input-xs input-bordered w-full"
                              value={item.mbs_item_number}
                              onChange={(e) => {
                                handleItemChange(index, 'mbs_item_number', e.target.value);
                                setMbsSearch(e.target.value);
                                setCurrentItemIndex(index);
                                setShowMbsSearch(true);
                              }}
                              placeholder="Search..."
                            />
                            {showMbsSearch && currentItemIndex === index && mbsItems.length > 0 && (
                              <div className="absolute z-10 w-64 mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                {mbsItems.map(mbsItem => (
                                  <div key={mbsItem.id} className="p-2 hover:bg-base-200 cursor-pointer" onClick={() => handleMbsItemSelect(mbsItem, index)}>
                                    <div className="font-semibold text-sm">{mbsItem.item_number}</div>
                                    <div className="text-xs opacity-60">{mbsItem.description}</div>
                                    <div className="text-xs font-semibold text-primary">{formatCurrency(mbsItem.fee)}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <input type="text" className="input input-xs input-bordered w-full" value={item.description} onChange={(e) => handleItemChange(index, 'description', e.target.value)} placeholder="Description" />
                        </td>
                        <td>
                          <input type="number" className="input input-xs input-bordered w-full" value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)} min="1" />
                        </td>
                        <td>
                          <input type="number" className="input input-xs input-bordered w-full" value={item.unit_price} onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)} min="0" step="0.01" />
                        </td>
                        <td className="font-semibold">${item.total.toFixed(2)}</td>
                        <td>
                          <button onClick={() => removeItem(index)} className="btn btn-xs btn-ghost btn-circle text-error" disabled={items.length === 1}>
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button onClick={addItem} className="btn btn-outline btn-xs mt-2">
                <FaPlus className="mr-1" /> Add Item
              </button>

              {/* Notes Section */}
              <div className="divider my-4">Notes</div>
              <div className="form-control">
                <label className="label py-1">
                  <span className="label-text text-sm font-semibold">Invoice Notes</span>
                  <span className="label-text-alt text-xs opacity-60">Internal notes, payment terms, special instructions</span>
                </label>
                <textarea 
                  className="textarea textarea-bordered h-24" 
                  placeholder="Add any notes about this invoice..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>

              {/* Summary Row */}
              <div className="flex justify-end mt-4">
                <div className="w-64 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span className="font-semibold">{formatCurrency(calculateSubtotal())}</span>
                  </div>
                  <div className="flex justify-between opacity-60">
                    <span>GST (10%):</span>
                    <span>{formatCurrency(calculateGST())}</span>
                  </div>
                  <div className="divider my-1"></div>
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total:</span>
                    <span className="text-primary">{formatCurrency(calculateTotal())}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Sidebar - Patient Info & Recent Activity */}
      <div className="w-80 bg-base-100 border-l border-base-300 overflow-y-auto">
        <div className="p-4">
          <h2 className="text-lg font-bold mb-4">Patient Information</h2>

          {/* Patient Search */}
          <div className="form-control mb-4">
            <input
              type="text"
              placeholder="Quick search patient..."
              className="input input-sm input-bordered"
              value={patientSearch}
              onChange={(e) => setPatientSearch(e.target.value)}
            />
          </div>

          {/* Selected Patient Info */}
          {selectedPatient && (
            <div className="mb-6 p-3 bg-base-200 rounded-lg">
              <div className="font-semibold text-base">{selectedPatient.first_name} {selectedPatient.last_name}</div>
              <div className="text-sm opacity-60 mt-1">
                <div>ID: {selectedPatient.patient_id}</div>
                <div>DOB: {moment(selectedPatient.date_of_birth).format('DD/MM/YYYY')}</div>
                {selectedPatient.insurance_type && <div>Insurance: {selectedPatient.insurance_type}</div>}
              </div>
            </div>
          )}

          {/* Recent Procedures/Consults */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-sm">Recent Activity</h3>
              {recentActivity.length > 0 && (
                <span className="badge badge-sm badge-primary">{recentActivity.length}</span>
              )}
            </div>
            {recentActivity.length > 0 ? (
              <div className="space-y-2">
                {recentActivity.map(activity => (
                  <div key={activity.id} className="p-2 bg-base-200 rounded text-sm hover:bg-base-300 transition-colors">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{activity.description}</div>
                        <div className="text-xs opacity-60 mt-0.5">
                          {moment(activity.date).format('DD/MM/YYYY')} • {activity.provider}
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          <span className={`badge badge-xs ${activity.type === 'procedure' ? 'badge-accent' : 'badge-info'}`}>
                            {activity.type}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm opacity-60 text-center py-8 bg-base-200 rounded-lg">
                {selectedPatient ? (
                  <>
                    <div className="text-2xl mb-2">📋</div>
                    <div>No recent activity found</div>
                  </>
                ) : (
                  <>
                    <div className="text-2xl mb-2">👤</div>
                    <div>Select a patient to view</div>
                    <div>recent activity</div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceCreate;
